import { useFlagValue } from '@labrav/flags'
import { isUndefined } from 'lodash'
import { useSelector } from 'react-redux'
import type { Product, SubscribeProducts } from '../../types'
import { useRegisteredOppSyncOnboardingStatus } from './oppsyncOnboardingStatusReader'
import { useFlyOutOnboardingStatus } from './useFlyOutOnboardingStatus'
import { useUserType } from './useUserType'

type AnyOneOnboardingState = {
  flyoutSellerInfo?: unknown
  flyOutSellerInfoMicrosoft?: unknown
  AccountData?: {
    user?: {
      partnerData?: {
        subscribedProducts?: SubscribeProducts
      } | null
    }
    admin?: {
      partnerData?: {
        subscribedProducts?: SubscribeProducts
      } | null
    }
  }
}

export const useAnyOneOnboardingCompeted = (
  activeProducts?: Product[] | string[]
) => {
  const { loading: isFlagCosellLoading, value: cosellEnabled } =
    useFlagValue('cosell')
  const isCosellEnabled = !isFlagCosellLoading && cosellEnabled

  const { flyoutSellerInfo } = useSelector((state: AnyOneOnboardingState) => ({
    flyoutSellerInfo: state.flyoutSellerInfo,
  }))
  const { microsoftSellerInfo } = useSelector(
    (state: AnyOneOnboardingState) => ({
      microsoftSellerInfo: state.flyOutSellerInfoMicrosoft,
    })
  )
  const { onboardingComplete, isNewPartner } =
    useRegisteredOppSyncOnboardingStatus(isCosellEnabled)

  const { onboardingComplete: flyoutOnboardingComplete } =
    useFlyOutOnboardingStatus()
  const userType = useUserType()
  const subscribedProducts = useSelector(
    (state: AnyOneOnboardingState): SubscribeProducts =>
      state?.AccountData?.[userType]?.partnerData?.subscribedProducts || {}
  )

  const flyoutProduct = subscribedProducts.FLYOUT
  const flyoutSubscribedCloudProviders = flyoutProduct?.cloudProviders || []
  const isOppsyncOnboarded = onboardingComplete || !isNewPartner
  const isFlyoutOnboarded = flyoutOnboardingComplete

  const isAnyOneOnboardingCompeted = isOppsyncOnboarded || isFlyoutOnboarded
  const firstProduct =
    activeProducts &&
    (activeProducts?.length >= 2
      ? getFirstOnboardedProduct({
          subscribedProducts,
          isOppsyncOnboarded,
          isFlyoutOnboarded,
          activeProducts,
        })
      : activeProducts[0])
  return {
    isAnyOneOnboardingCompeted,
    firstProduct,
    loading:
      firstProduct === 'flyout'
        ? (flyoutSubscribedCloudProviders?.includes('AWS') &&
            isUndefined(flyoutSellerInfo)) ||
          (flyoutSubscribedCloudProviders?.includes('AZURE') &&
            isUndefined(microsoftSellerInfo))
        : false,
  }
}

const getFirstOnboardedProduct = ({
  subscribedProducts,
  isOppsyncOnboarded,
  isFlyoutOnboarded,
  activeProducts,
}: {
  subscribedProducts: Partial<SubscribeProducts>
  isFlyoutOnboarded: boolean
  isOppsyncOnboarded: boolean
  activeProducts?: Product[] | string[]
}) => {
  if (
    activeProducts &&
    ((activeProducts[0] === 'flyout' && isFlyoutOnboarded) ||
      (activeProducts[0] === 'oppsync' && isOppsyncOnboarded))
  ) {
    return activeProducts[0]
  }

  if (
    activeProducts?.includes('flyout') &&
    subscribedProducts?.FLYOUT &&
    isFlyoutOnboarded
  ) {
    return 'flyout' as Product
  }

  if (
    activeProducts?.includes('oppsync') &&
    subscribedProducts?.OPPSYNC &&
    isOppsyncOnboarded
  ) {
    return 'oppsync' as Product
  }

  if (activeProducts && activeProducts?.length) {
    return activeProducts[0]
  }
}
